import React, { useCallback, memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplate } from '@/contexts/TemplateContext';
import * as bookingApi from '@/services/bookingApi';

interface CustomerDetailsFormProps {
  customerInfo: {
    name: string;
    email: string;
    phone: string;
    notes: string;
  };
  onChange: (info: Partial<CustomerDetailsFormProps['customerInfo']>) => void;
  requiresPhone?: boolean;
  requiresEmail?: boolean;
  clientVerificationEnabled?: boolean;
  imagePaths?: string[];
  onImageUploaded?: (path: string) => void;
  onImageRemoved?: (path: string) => void;
  uploadContext?: {
    profileId: string;
    date: string;
    time: string;
    sessionId: string;
    assignedUserId?: string | null;
  };
}

const CustomerDetailsFormInner: React.FC<CustomerDetailsFormProps> = ({
  customerInfo,
  onChange,
  requiresPhone = true,
  requiresEmail = false,
  clientVerificationEnabled = true,
  imagePaths = [],
  onImageUploaded,
  onImageRemoved,
  uploadContext,
}) => {
  const { colors } = useTemplate();
  const { t } = useTranslation('booking');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleChange = useCallback(
    (field: keyof typeof customerInfo) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ [field]: e.target.value });
      },
    [onChange]
  );

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onImageUploaded) return;
    if (imagePaths.length >= 3) {
      setUploadError(t('customer.referenceImages.maxReached'));
      return;
    }

    if (!uploadContext?.profileId || !uploadContext.date || !uploadContext.time || !uploadContext.sessionId) {
      setUploadError(t('customer.referenceImages.uploadFailed'));
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const path = await bookingApi.uploadBookingImage(file, uploadContext);
      onImageUploaded(path);
    } catch (err: unknown) {
      setUploadError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          t('customer.referenceImages.uploadFailed')
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold" style={{ color: colors.colors.text }}>
          {t('customer.title')}
        </h2>
        <p className="mt-2" style={{ color: colors.colors.textMuted }}>
          {t('customer.subtitle')}
        </p>
      </div>

      <div
        className="rounded-lg shadow-sm border p-6 space-y-4"
        style={{ backgroundColor: colors.colors.background }}
      >
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.colors.text }}>
            {t('customer.form.name.label')} <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            value={customerInfo.name}
            onChange={handleChange('name')}
            placeholder={t('customer.form.name.placeholder')}
            required
            className="w-full px-4 py-2 rounded-lg focus:ring-ring"
            style={{ borderColor: colors.colors.secondary }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.colors.text }}>
            {t('customer.form.email.label')}{' '}
            {requiresEmail ? (
              <span style={{ color: '#ef4444' }}>*</span>
            ) : (
              <span style={{ color: colors.colors.textMuted }}>{t('customer.form.phone.optional')}</span>
            )}
          </label>
          <input
            type="email"
            value={customerInfo.email}
            onChange={handleChange('email')}
            placeholder={t('customer.form.email.placeholder')}
            required={requiresEmail}
            className="w-full px-4 py-2 rounded-lg focus:ring-ring"
            style={{ borderColor: colors.colors.secondary }}
          />
          <p className="text-sm mt-1" style={{ color: colors.colors.textMuted }}>
            {requiresEmail
              ? t('customer.form.email.note')
              : 'Optional — used for confirmations if provided.'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.colors.text }}>
            {t('customer.form.phone.label')}{' '}
            {requiresPhone ? (
              <span style={{ color: '#ef4444' }}>*</span>
            ) : (
              <span style={{ color: colors.colors.textMuted }}>{t('customer.form.phone.optional')}</span>
            )}
          </label>
          <input
            type="tel"
            value={customerInfo.phone}
            onChange={handleChange('phone')}
            placeholder={t('customer.form.phone.placeholder')}
            required={requiresPhone}
            className="w-full px-4 py-2 rounded-lg focus:ring-ring"
            style={{ borderColor: colors.colors.secondary }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.colors.text }}>
            {t('customer.form.notes.label')}{' '}
            <span style={{ color: colors.colors.textMuted }}>{t('customer.form.notes.optional')}</span>
          </label>
          <textarea
            value={customerInfo.notes}
            onChange={handleChange('notes')}
            placeholder={t('customer.form.notes.placeholder')}
            rows={3}
            className="w-full px-4 py-2 rounded-lg resize-none focus:ring-ring"
            style={{ borderColor: colors.colors.secondary }}
          />
        </div>

        {onImageUploaded && (
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
              {t('customer.referenceImages.label')}{' '}
              <span style={{ color: colors.colors.textMuted }}>{t('customer.referenceImages.optional')}</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {imagePaths.map((path) => (
                <div key={path} className="relative">
                  <div className="w-20 h-20 rounded-lg border bg-muted flex items-center justify-center text-xs text-muted-foreground overflow-hidden">
                    {path.split('/').pop()}
                  </div>
                  {onImageRemoved && (
                    <button
                      type="button"
                      onClick={() => onImageRemoved(path)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            {imagePaths.length < 3 && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-muted disabled:opacity-50"
                >
                  {uploading ? t('customer.referenceImages.uploading') : t('customer.referenceImages.uploadButton')}
                </button>
              </>
            )}
            {uploadError && <p className="text-sm text-red-600 mt-2">{uploadError}</p>}
          </div>
        )}
      </div>

      <div
        className="rounded-lg p-4"
        style={{ backgroundColor: colors.colors.accent, borderColor: colors.colors.accent }}
      >
        <p className="text-sm" style={{ color: colors.colors.text }}>
          {!clientVerificationEnabled
            ? 'Your request will be reviewed by the provider. They may contact you by phone to confirm.'
            : requiresEmail
              ? t('customer.verificationNote')
              : t('customer.phoneVerificationNote')}
        </p>
      </div>
    </div>
  );
};

export const CustomerDetailsForm = memo(CustomerDetailsFormInner);
